import React from 'react';
import type { Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import CheckIcon from '@material-ui/icons/CheckCircle';
import Description from '@material-ui/icons/Description';
import { makeStyles } from '@material-ui/styles';
import { colors } from '@skyslope/mache';
import cs from 'classnames';
import { useDispatch, useShallowSelector } from '../../lib/reduxHooks';
import * as actions from '../../store/applyTemplate/actions';

const useStyles = makeStyles((theme: Theme) => ({
  header: {
    height: 68,
    display: 'flex',
    alignItems: 'center',
    paddingLeft: theme.spacing(4),
    borderBottom: `1px solid ${colors.grey[400]}`,
  },
  icon: {
    marginRight: theme.spacing(2),
    color: colors.grey[500],
    width: 28,
    height: 28,
  },
  document: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    borderBottom: `1px solid ${colors.grey[400]}`,
    userSelect: 'none',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  selectedDocument: {
    backgroundColor: '#FFFFFF',
  },
  overflowContainer: {
    overflow: 'hidden',
  },
  fileName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: 1,
  },
  iconWrapper: {
    position: 'relative',
    display: 'flex',
  },
  check: {
    color: '#02AF90',
    fontSize: 18,
    position: 'absolute',
    right: 14,
    top: -4,
    backgroundColor: '#FFFFFF',
    borderRadius: '50%',
  },
}));

export function Documents(props: React.ComponentProps<'div'>) {
  const { className, ...rest } = props;
  const classes = useStyles();
  const dispatch = useDispatch();

  const { documents, selectedDocumentId, appliedTemplates } = useShallowSelector((state) => ({
    documents: state.applyTemplate.envelope!.documents,
    selectedDocumentId: state.applyTemplate.selectedDocumentId!,
    appliedTemplates: state.applyTemplate.appliedTemplates,
  }));

  function selectDocument(documentId: string) {
    if (documentId === selectedDocumentId) return;
    dispatch(actions.selectDocument(documentId));
  }

  return (
    <div {...rest} className={cs('Documents', className)}>
      <div className={classes.header}>
        <Typography variant="subtitle1">YOUR UPLOADED DOCUMENTS</Typography>
      </div>
      {documents.map((document) => (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
          data-spec="apply-template-document"
          key={document.documentId}
          className={`${classes.document} ${
            document.documentId === selectedDocumentId ? classes.selectedDocument : ''
          }`}
          onClick={() => {
            selectDocument(document.documentId);
          }}
        >
          <div className={classes.iconWrapper}>
            <Description className={classes.icon} />
            {appliedTemplates[document.documentId] && <CheckIcon className={classes.check} />}
          </div>
          <div className={classes.overflowContainer}>
            <Typography variant="body1" className={classes.fileName}>
              {document.fileName}
            </Typography>
          </div>
        </div>
      ))}
    </div>
  );
}
